import React, { useState, useEffect, lazy } from 'react';
import { ScreeningTable } from './components/features/hiring/ScreeningTable.js';
import { CreatorTable } from './components/features/creators/CreatorTable.js';
import { ApplicantDetails } from './components/features/hiring/ApplicantDetails.js';
import { CreatorDetails } from './components/features/creators/CreatorDetails';
import { StatusLegend } from './components/common/StatusLegend.js';
import { Analytics } from './components/features/analytics/Analytics';
import type { Applicant } from './types/applicant.js';
import type { Creator } from './types/creator.js';
import { ApplicantStatus, RatingStatus } from './types/enums.js';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from './lib/api/fetchWithAuth.js';
import { LoginPage } from './components/auth/LoginPage.js';
import { OnboardingFlow } from './components/auth/OnboardingFlow.js';
import { Toaster, toast } from 'react-hot-toast';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { ApplicationForm } from './components/features/hiring/ApplicationForm';
import { HighInterestCandidateApplication } from './components/features/hiring/HighInterestCandidateApplication';
import { HighInterestCandidateApplicationSpanish } from './components/features/hiring/HighInterestCandidateApplicationSpanish';
import { ThankYouPage } from './components/features/hiring/ThankYouPage.js';
import { User } from 'lucide-react';
import { TopBar } from './components/layout/TopBar.js';
import { BrandSelector } from './components/layout/BrandSelector.js';
import { CreateBrandModal } from './components/modals/CreateBrandModal.js';
import { BrandCalculator } from './components/features/misc/BrandCalculator.js';
import { OnboardingPage } from './pages/OnboardingPage.js';
import { OnboardingConfirmationPage } from './pages/OnboardingConfirmationPage.js';
import { PrivacyPolicy } from './components/misc/PrivacyPolicy.js';
import { TermsOfService } from './components/misc/TermsOfService.js';
import { AddCreatorModal } from './components/modals/AddCreatorModal.js';
import { SendOfferModal } from './components/modals/SendOfferModal.js';
import { TikTokLeaderboard } from './components/features/leaderboard/TikTokLeaderboard.js';
import GmailSettingsPage from './pages/settings/gmail';
import MembersSettingsPage from './pages/settings/members';
import { ContentBriefUnified } from './components/features/hiring/ContentBriefUnified';
import { TikTokAccountSetup } from './components/features/hiring/TikTokAccountSetup';
import { InstagramAccountSetup } from './components/features/hiring/InstagramAccountSetup';
import { YouTubeShortsAccountSetup } from './components/features/hiring/YouTubeShortsAccountSetup';

interface Brand {
  id: string;
  name: string;
  organizationId: string;
  subdomain: string;
  settings?: {
    id: string;
    brandId: string;
    createdAt: Date;
    updatedAt: Date;
  };
  applicants?: Applicant[];
  creators?: Creator[];
}

const GmailCallback = lazy(() => import('./pages/settings/gmail/callback.js'));

function App() {
  const { 
    isAuthenticated, 
    logout, 
    user, 
    isLoading: isAuthLoading,
    getAccessTokenSilently
  } = useAuth0();

  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);
  const [isLoadingApplicants, setIsLoadingApplicants] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [userBrands, setUserBrands] = useState<Brand[]>([]);
  const [showCreateBrand, setShowCreateBrand] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [creators, setCreators] = useState<Creator[]>([]);
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [isLoadingCreators] = useState(false);
  const [showAddCreator, setShowAddCreator] = useState(false);
  const [showSendOffer, setShowSendOffer] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserBrands();
    }
  }, [isAuthenticated]);

  const fetchUserBrands = async () => {
    try {
      setIsLoadingApplicants(true);
      const token = await getAccessTokenSilently();
      console.log('Fetching organizations...');
      const data = await fetchWithAuth('/api/organizations', { token });
      console.log('Organizations:', data);
      
      // Check if user needs onboarding
      if (data.length === 0) {
        setNeedsOnboarding(true);
        return;
      }

      // Get first organization's brands
      const org = data[0];
      const brands = org.brands || [];
      console.log('Brands:', brands);
      setUserBrands(brands);
      
      // If user has brands, select the first one and set its applicants
      if (brands.length > 0) {
        const firstBrand = brands[0];
        console.log('Setting selected brand:', firstBrand);
        setSelectedBrand(firstBrand);
        setApplicants(firstBrand.applicants || []);
        setCreators(firstBrand.creators || []);
      } else {
        console.log('No brands found, showing create brand modal');
        setShowCreateBrand(true);
      }
    } catch (err) {
      console.error('Failed to fetch user brands:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoadingApplicants(false);
    }
  };

  // Remove the separate fetchApplicants function since data comes with brands now
  useEffect(() => {
    if (selectedBrand) {
      console.log('Selected brand changed, setting applicants:', selectedBrand.applicants);
      setApplicants(selectedBrand.applicants || []);
      setCreators(selectedBrand.creators || []);
    }
  }, [selectedBrand]);

  const updateApplicantStatus = (applicantId: string, newStatus: ApplicantStatus) => {
    const now = new Date();
    setApplicants(prevApplicants => 
      prevApplicants.map(applicant => 
        applicant.id === applicantId 
          ? {
              ...applicant,
              status: newStatus,
              stageLastUpdated: now
            } as Applicant
          : applicant
      )
    );
  };

  const updateApplicantRating = async (applicantId: string, rating: number) => {
    const now = new Date();
    try {
      // Optimistically update UI
      setApplicants(prevApplicants =>
        prevApplicants.map(applicant =>
          applicant.id === applicantId
            ? {
                ...applicant,
                rating,
                ratingStatus: RatingStatus.RATED,
                status: rating === 1 ? ApplicantStatus.REJECTED : applicant.status
              } as Applicant
            : applicant
        )
      );

      // Make API call
      const token = await getAccessTokenSilently();
      await fetchWithAuth(
        `/api/organizations/${selectedBrand?.organizationId}/brands/${selectedBrand?.id}/applicants/${applicantId}/rating`,
        {
          method: 'PUT',
          token,
          body: JSON.stringify({ rating })
        }
      );

      // Show success toast
      toast.success('Rating updated successfully');

      // If rating is 2 or higher, show sample request modal
      const applicant = applicants.find(a => a.id === applicantId);
      if (rating >= 2 && applicant && !applicant.sampleRequestSent) {
        // Find the applicant in the ScreeningTable and trigger its sample request modal
        const event = new CustomEvent('showSampleRequestModal', { 
          detail: { applicantId } 
        });
        window.dispatchEvent(event);
      }
    } catch (error) {
      console.error('Failed to update rating:', error);
      // Revert optimistic update on error
      await fetchUserBrands();
      toast.error('Failed to update rating');
    }
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      {(() => { console.log('App render - Auth state:', { isAuthenticated, isLoading: isAuthLoading }); return null; })()}
      <Routes>
        {/* Public routes accessible to everyone */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/calculator" element={<BrandCalculator />} />
        <Route path="/apply/thank-you" element={<ThankYouPage />} />
        <Route path="/apply" element={<ApplicationForm />} />
        <Route path="/high-interest-candidate-application" element={<HighInterestCandidateApplication />} />
        <Route path="/high-interest-candidate-application-spanish" element={<HighInterestCandidateApplicationSpanish />} />
        <Route path="/content-brief" element={<ContentBriefUnified />} />
        <Route path="/tiktok-account-setup" element={<TikTokAccountSetup />} />
        <Route path="/instagram-account-setup" element={<InstagramAccountSetup />} />
        <Route path="/youtube-shorts-account-setup" element={<YouTubeShortsAccountSetup />} />
        <Route path="/onboarding/:token" element={<OnboardingPage />} />
        <Route path="/onboarding/confirmation" element={<OnboardingConfirmationPage />} />
        <Route path="/leaderboards/tiktok" element={<TikTokLeaderboard />} />

        {/* Handle Gmail callback before auth check */}
        <Route path="/settings/gmail/callback" element={
          <div>
            {(() => { console.log('Rendering Gmail callback route'); return null; })()}
            <React.Suspense fallback={
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" />
              </div>
            }>
              <GmailCallback />
            </React.Suspense>
          </div>
        } />

        {/* All other routes require authentication */}
        <Route path="/*" element={
          !isAuthenticated ? (
            <>{(() => { console.log('Rendering LoginPage - not authenticated'); return null; })()}<LoginPage /></>
          ) : needsOnboarding ? (
            <OnboardingFlow onComplete={() => {
              setNeedsOnboarding(false);
              fetchUserBrands();
            }} />
          ) : (
            <div className="min-h-screen bg-gray-900 text-gray-100">
                  <TopBar selectedBrand={selectedBrand} />
                  <div className="h-[calc(100vh-4rem)] pt-16">
                    <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                      <Routes>
                        <Route path="/settings/organizations/:orgId/gmail" element={<GmailSettingsPage />} />
                        <Route path="/settings/organizations/:orgId/members" element={<MembersSettingsPage />} />
                        <Route path="/" element={
                          <>
                            <div className="flex justify-between items-center mb-6">
                              <h1 className="text-2xl font-bold text-gray-100">Applicant Screening</h1>

                              <div className="flex items-center space-x-4">
                                <BrandSelector
                                  brands={userBrands}
                                  selectedBrand={selectedBrand}
                                  onSelectBrand={setSelectedBrand}
                                  onCreateNew={() => setShowCreateBrand(true)}
                                />
                                <input
                                  type="text"
                                  placeholder="Search applicants..."
                                  className="px-4 py-2 border border-gray-700 rounded-md bg-gray-800 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <button 
                                  type="button"
                                  onClick={() => setShowSendOffer(true)}
                                  className="px-4 py-2 bg-indigo-600 text-gray-100 rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors">
                                  Send Offer
                                </button>
                                <div className="relative">
                                  <button
                                    type="button"
                                    onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                                    className="p-2 rounded-md hover:bg-gray-700 transition-colors"
                                  >
                                    <User className="w-5 h-5 text-gray-300" />
                                  </button>

                                  {accountMenuOpen && (
                                    <div 
                                      className="absolute top-full right-0 mt-2 w-48 rounded-md shadow-lg bg-gray-800 ring-1 ring-black ring-opacity-5 z-50"
                                      onMouseLeave={() => setAccountMenuOpen(false)}
                                    >
                                      <div className="py-1">
                                        <div className="px-4 py-2 text-sm text-gray-400 border-b border-gray-700">
                                          {user?.name}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={handleLogout}
                                          className="block w-full text-left px-4 py-2 text-sm text-gray-100 hover:bg-gray-700"
                                        >
                                          Log Out
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="h-[calc(100vh-16rem)]">
                              {isLoadingApplicants ? (
                                <div className="h-full flex items-center justify-center">
                                  <div className="flex flex-col items-center space-y-4">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" />
                                    <div className="text-gray-400">Loading data...</div>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="h-[calc(100%-100px)]">
                                    <ScreeningTable
                                      applicants={applicants}
                                      onViewDetails={(applicant) => setSelectedApplicant(applicant)}
                                      onUpdate={fetchUserBrands}
                                      onStatusUpdate={updateApplicantStatus}
                                      onRatingUpdate={updateApplicantRating}
                                      setApplicants={setApplicants}
                                      brandId={selectedBrand?.id || ''}
                                      orgId={selectedBrand?.organizationId || ''}
                                    />
                                  </div>
                                  <div className="mt-5">
                                    <StatusLegend type="applicant" />
                                  </div>
                                </>
                              )}
                            </div>
                          </>
                        } />
                        <Route path="/creators" element={
                          <>
                            <div className="flex justify-between items-center mb-6">
                              <h1 className="text-2xl font-bold text-gray-100">Creator Management</h1>

                              <div className="flex items-center space-x-4">
                                <BrandSelector
                                  brands={userBrands}
                                  selectedBrand={selectedBrand}
                                  onSelectBrand={setSelectedBrand}
                                  onCreateNew={() => setShowCreateBrand(true)}
                                />
                                <input
                                  type="text"
                                  placeholder="Search creators..."
                                  className="px-4 py-2 border border-gray-700 rounded-md bg-gray-800 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <button 
                                  type="button"
                                  onClick={() => setShowAddCreator(true)}
                                  className="px-4 py-2 bg-indigo-600 text-gray-100 rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors">
                                  Add Creator
                                </button>
                                <div className="relative">
                                  <button
                                    type="button"
                                    onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                                    className="p-2 rounded-md hover:bg-gray-700 transition-colors"
                                  >
                                    <User className="w-5 h-5 text-gray-300" />
                                  </button>

                                  {accountMenuOpen && (
                                    <div 
                                      className="absolute top-full right-0 mt-2 w-48 rounded-md shadow-lg bg-gray-800 ring-1 ring-black ring-opacity-5 z-50"
                                      onMouseLeave={() => setAccountMenuOpen(false)}
                                    >
                                      <div className="py-1">
                                        <div className="px-4 py-2 text-sm text-gray-400 border-b border-gray-700">
                                          {user?.name}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={handleLogout}
                                          className="block w-full text-left px-4 py-2 text-sm text-gray-100 hover:bg-gray-700"
                                        >
                                          Log Out
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div>
                              {isLoadingCreators ? (
                                <div className="h-[calc(100vh-300px)] flex items-center justify-center">
                                  <div className="flex flex-col items-center space-y-4">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" />
                                    <div className="text-gray-400">Loading data...</div>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="max-h-[calc(100vh-300px)] overflow-y-auto mb-4">
                                    <CreatorTable
                                      creators={creators}
                                      onViewDetails={(creator) => setSelectedCreator(creator)}
                                      onUpdate={fetchUserBrands}
                                      brandId={selectedBrand?.id || ''}
                                      orgId={selectedBrand?.organizationId || ''}
                                    />
                                  </div>
                                  <div className="mt-5">
                                    <StatusLegend type="creator" />
                                  </div>
                                </>
                              )}
                            </div>
                          </>
                        } />
                        <Route path="/templates" element={
                          <div>
                            <h1 className="text-2xl font-bold text-gray-100">Email Templates</h1>
                            <p className="text-gray-400 mt-4">Email templates coming soon...</p>
                          </div>
                        } />
                        <Route path="/analytics" element={
                          <div>
                            <h1 className="text-2xl font-bold text-gray-100 mb-6">Analytics</h1>
                            <Analytics 
                              applicants={applicants} 
                              brands={userBrands}
                              selectedBrand={selectedBrand}
                              onSelectBrand={setSelectedBrand}
                              onCreateNew={() => setShowCreateBrand(true)}
                            />
                          </div>
                        } />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </div>
                  </div>

                  {selectedApplicant && (
                    <ApplicantDetails
                      applicant={selectedApplicant}
                      onClose={() => setSelectedApplicant(null)}
                    />
                  )}
                </div>
              )
            } />
      </Routes>
      
      <CreateBrandModal
        isOpen={showCreateBrand}
        onClose={() => setShowCreateBrand(false)}
        onCreateBrand={async (brandData) => {
          const token = await getAccessTokenSilently();
          console.log('Creating brand with data:', brandData);
          const orgsData = await fetchWithAuth('/api/organizations', { token });
          console.log('Organizations for brand creation:', orgsData);
          if (orgsData.length === 0) {
            // Create organization if none exists
            console.log('No organizations found, creating new organization');
            const newOrg = await fetchWithAuth('/api/organizations', {
              method: 'POST',
              token,
              body: JSON.stringify({ name: brandData.name })
            });
            console.log('Created new organization:', newOrg);
            // Create brand in the new organization
            const newBrand = await fetchWithAuth(`/api/organizations/${newOrg.id}/brands`, {
              method: 'POST',
              token,
              body: JSON.stringify({
                name: brandData.name,
                description: brandData.description,
                subdomain: brandData.subdomain
              })
            });
            console.log('Created new brand in new organization:', newBrand);
            setUserBrands([{ ...newBrand, organizationId: newOrg.id }]);
            setSelectedBrand({ ...newBrand, organizationId: newOrg.id });
          } else {
            // Create brand in the first organization
            console.log('Creating brand in existing organization:', orgsData[0]);
            const newBrand = await fetchWithAuth(`/api/organizations/${orgsData[0].id}/brands`, {
              method: 'POST',
              token,
              body: JSON.stringify({
                name: brandData.name,
                description: brandData.description,
                subdomain: brandData.subdomain
              })
            });
            console.log('Created new brand in existing organization:', newBrand);
            setUserBrands([...userBrands, { ...newBrand, organizationId: orgsData[0].id }]);
            setSelectedBrand({ ...newBrand, organizationId: orgsData[0].id });
          }
          setShowCreateBrand(false);
        }}
      />
      
      <Toaster 
        position="bottom-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#374151',
            color: '#fff',
          },
        }}
      />

      {selectedCreator && (
        <CreatorDetails
          creator={selectedCreator}
          onClose={() => setSelectedCreator(null)}
        />
      )}

      {selectedBrand && (
        <AddCreatorModal
          isOpen={showAddCreator}
          onClose={() => setShowAddCreator(false)}
          onCreatorAdded={fetchUserBrands}
          brandId={selectedBrand.id}
          orgId={selectedBrand.organizationId}
        />
      )}

      {selectedBrand && (
        <SendOfferModal
          isOpen={showSendOffer}
          onClose={() => setShowSendOffer(false)}
          onOfferSent={fetchUserBrands}
          brandId={selectedBrand.id}
          orgId={selectedBrand.organizationId}
        />
      )}
    </BrowserRouter>
  );
}

export default App;